import {createRootRoute, Outlet, useLocation, useNavigate} from '@tanstack/react-router'
import {AppShell, Burger, Button, Group, Title, Tooltip, Text, Stack, TableOfContents} from "@mantine/core";
import Navbar from "@components/navbar.tsx";
import {useDisclosure} from "@mantine/hooks";
import {IconList, IconLogout} from "@tabler/icons-react";
import {useUser} from "@state/client/user.store.ts";
import {useEffect, useRef, useState} from 'react';
import {formatDuration, interval, intervalToDuration} from "date-fns"
import {cs} from "date-fns/locale";

export const Route = createRootRoute({
	component: () => <Root/>
})

const getFormattedDuration = (a: Date, b: Date) => {
	const intervalV = interval(a, b)

	return formatDuration(intervalToDuration(intervalV), {format: ["hours", "minutes"], locale: cs, zero: false})
}

function Root() {
	const [opened, {toggle: toggleMenu, close: closeMenu}] = useDisclosure(false)
	const [enableToc, {toggle: toggleToc}] = useDisclosure(true)
	const path = useLocation({select: (l) => l.pathname})
	const userState = useUser()
	const [expiresIn, setExpiresIn] = useState(getFormattedDuration(new Date(), userState.expiresAt ?? new Date()))
	const navigate = useNavigate()

	const checkExpFn = () => {
		setExpiresIn(getFormattedDuration(new Date(), userState.expiresAt ?? new Date()))
	}

	const intervalRef = useRef<ReturnType<typeof setInterval>>(null)
	const reloadToc = useRef(()=>{})

	useEffect(() => {
		if (intervalRef.current) clearInterval(intervalRef.current)
		intervalRef.current = setInterval(checkExpFn, 30000)
		// Only place that ref shall be modified is here, and the timeout should be reset when login state changes
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userState])

	const onLogout = async () => {
		userState.logout()
		await navigate({to: "/login"})
	}

	useEffect(() => {
		if (["/settings"].includes(path) && !enableToc) toggleToc()
		else if (enableToc) toggleToc()

		closeMenu()
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [path]);

	useEffect(() => {
		setTimeout(()=>{
			if(enableToc)
				reloadToc.current()
		}, 100)
	}, [enableToc]);

	return (
		<AppShell
			header={{height: 60}}
			navbar={{width: 300, breakpoint: "sm", collapsed: {mobile: !opened}}}
			aside={{width: 300, breakpoint: "sm", collapsed: {mobile: true, desktop: !enableToc}}}
			padding={path != "/login" ? "md" : undefined}
			disabled={path == "/login"}
		>
			<AppShell.Header display={"flex"}>
				<Group w={"100%"} px={"md"}>
					<Burger
						opened={opened}
						onClick={toggleMenu}
						hiddenFrom="sm"
						size="sm"
					/>
					<Title order={2} style={{display: "block"}}>Dobřanská zmrzlina APP</Title>
					<Group ml={"auto"} visibleFrom={"md"}>
						<Stack gap={0}>
							<Text ta={"right"}>{userState.username}</Text>
							<Text size={"sm"}>Odhlášení za {expiresIn}</Text>
						</Stack>
						<Tooltip label={"Odhlásit se"}>
							<Button variant={"outline"} onClick={onLogout}><IconLogout/></Button>
						</Tooltip>
					</Group>
				</Group>
			</AppShell.Header>

			<AppShell.Navbar p="md"><Navbar/></AppShell.Navbar>
			<AppShell.Main>
				<Outlet/>
			</AppShell.Main>
			{enableToc && <AppShell.Aside>
                <Group align={"center"} gap={".25em"} px={"md"}>
                    <IconList/>
                    <Title order={2} my={"xs"}>Obsah</Title>
                </Group>
                <TableOfContents
                    scrollSpyOptions={{
						selector: ".toc-item"
					}}
                    getControlProps={({data}) => ({
						onClick: () => data.getNode().scrollIntoView(),
						children: data.value,
					})}

                    py={"md"}
                    variant={"light"}
					reinitializeRef={reloadToc}
                />
            </AppShell.Aside>}
		</AppShell>
	)
}